'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { ValidationMessage } from '@/ui/form/validation-message/ValidationMessage.client'
import type React from 'react'
import { useId, useMemo } from 'react'
import { Select as AriaSelect, FieldError } from 'react-aria-components'
import { AbstractInput, type PublicInputProps } from '../../_shared/AbstractInput.client'
import { SharedUILabel } from '../../_shared/SharedUILabel.client'
import type { SelectListItemData } from './_components/SelectListItem.client'
import type { SelectListSectionData } from './_components/SelectListSection.client'
import { SelectMultipleTrigger } from './_components/SelectMultipleTrigger.client'
import { SelectPopover } from './_components/SelectPopover.client'
import { SelectSingleTrigger } from './_components/SelectSingleTrigger.client'
import {
  areAllItemsSelected,
  constrainSelectionCount,
  getDisabledSelectionKeys,
  getFlatItems,
  getListItems,
  getNextMultipleValue,
  getSelectableIds,
  isSameSelection,
  removeKeysFromValue,
  toSelectValue,
  toSingleFormValue,
} from './_utils/selectValue'
import { SELECT_ALL_ID } from './select.styles'
import { SelectSkeleton } from './SelectSkeleton'

export type { SelectListItemData, SelectListSectionData }

export type SelectProps = UiComponentProps<{
  placeholder?: string
  isDisabled?: boolean
  /** @default 'single' */
  selectionMode?: 'single' | 'multiple'
  isSearchable?: boolean
  searchPlaceholder?: string
  items: SelectListItemData[] | SelectListSectionData[]
  maxHeight?: number
  onValueChange?: (value: string | string[]) => void
  notificationNumber?: number
  trailingOutlet?: React.ReactElement
  isLoading?: boolean
  /** Multi + flat list only. Not stored in the form value. */
  selectAllLabel?: string
  /**
   * When false, the trigger shows `placeholder` instead of the selected value(s)
   * (tags in multiple, item label in single). @default true
   */
  withTags?: boolean
  /** Non-interactive message above the list, outside the scrollport (same zone as search). */
  headerMessage?: string
  /** Multiple only. Selected options cannot be unchecked below this count. */
  minSelected?: number
  /** Multiple only. Unselected options cannot be checked above this count. */
  maxSelected?: number
}>

type SelectInputProps = PublicInputProps<SelectProps, 'required'>

export function Select({
  className,
  name,
  label,
  items,
  placeholder,
  isDisabled,
  selectionMode = 'single',
  isSearchable = false,
  searchPlaceholder,
  maxHeight,
  onValueChange,
  notificationNumber,
  trailingOutlet,
  isLoading = false,
  selectAllLabel,
  withTags = true,
  headerMessage,
  minSelected,
  maxSelected,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  ...rules
}: SelectInputProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const { t } = useTranslation(['Select', 'searchInput'])
  const isMultiple = selectionMode === 'multiple'
  const flatItems = useMemo(
    () => getFlatItems(items, isMultiple, selectAllLabel),
    [isMultiple, items, selectAllLabel],
  )
  const allItemIds = useMemo(() => getSelectableIds(items), [items])
  const listItems = useMemo(
    () => getListItems(items, flatItems, selectAllLabel),
    [flatItems, items, selectAllLabel],
  )

  return (
    <AbstractInput
      {...rules}
      name={name}
      render={({ field, fieldState }) => {
        const showLoader = isLoading || fieldState.isValidating
        const hasError = !!fieldState.error?.message
        const shouldDisplayError = !showLoader && !field.isDisabled && hasError
        const disabled = !!(isDisabled || field.isDisabled)
        const selectValue = toSelectValue(selectionMode, field.value)
        const selectedIds = Array.isArray(selectValue) ? selectValue.map(String) : []
        const allSelected = areAllItemsSelected(allItemIds, selectedIds)
        const disabledKeys = getDisabledSelectionKeys({
          selectableIds: allItemIds,
          selectedIds,
          minSelected: isMultiple ? minSelected : undefined,
          maxSelected: isMultiple ? maxSelected : undefined,
          hasSelectAll: !!selectAllLabel,
        })
        const lockedTagIds = new Set(disabledKeys.filter((id) => selectedIds.includes(id)))
        const ariaValue =
          isMultiple && selectAllLabel && allSelected
            ? [SELECT_ALL_ID, ...selectedIds]
            : selectValue

        return (
          <div className={twMerge('w-full min-w-[15rem]', className)} data-testid={dataTestId}>
            <AriaSelect
              name={name}
              className="notranslate relative flex w-full flex-col gap-1"
              translate="no"
              selectionMode={selectionMode}
              value={ariaValue}
              disabledKeys={disabledKeys}
              onChange={(key) => {
                const unconstrained = Array.isArray(key)
                  ? getNextMultipleValue(key, selectAllLabel, allSelected, allItemIds)
                  : toSingleFormValue(key)
                const nextValue = Array.isArray(unconstrained)
                  ? constrainSelectionCount(unconstrained, selectedIds, minSelected, maxSelected)
                  : unconstrained

                field.onChange(nextValue)
                onValueChange?.(nextValue)
              }}
              onBlur={field.onBlur}
              isRequired={rules.required}
              isDisabled={disabled}
              isInvalid={shouldDisplayError}
              placeholder={placeholder}
              aria-label={ariaLabel}
            >
              {({ isOpen }) => (
                <>
                  {label && (
                    <SharedUILabel
                      className="mb-1 block"
                      inputId={inputId}
                      label={label}
                      required={!!rules.required}
                    />
                  )}
                  {/* flex-1: field takes remaining width next to trailingOutlet (tooltip).
                      flex-row-reverse: Popover --trigger-width tracks the field (last flex child). */}
                  <div className="flex w-full flex-row-reverse items-center gap-2">
                    {trailingOutlet}
                    <div className="min-w-0 flex-1">
                      {isMultiple ? (
                        <SelectMultipleTrigger
                          inputId={inputId}
                          isOpen={isOpen}
                          hasError={shouldDisplayError}
                          isDisabled={disabled}
                          showLoader={showLoader}
                          notificationNumber={notificationNumber}
                          withTags={withTags}
                          displayLabel={withTags ? undefined : placeholder}
                          tagsAriaLabel={t('Select:selectedItems.ariaLabel')}
                          lockedTagIds={lockedTagIds}
                          onRemoveKeys={(keys) => {
                            const next = constrainSelectionCount(
                              removeKeysFromValue(field.value, keys),
                              selectedIds,
                              minSelected,
                              maxSelected,
                            )
                            if (isSameSelection(next, selectedIds)) return false

                            field.onChange(next)
                            onValueChange?.(next)

                            return true
                          }}
                        />
                      ) : (
                        <SelectSingleTrigger
                          inputId={inputId}
                          isOpen={isOpen}
                          hasError={shouldDisplayError}
                          isDisabled={disabled}
                          showLoader={showLoader}
                          notificationNumber={notificationNumber}
                          displayLabel={withTags ? undefined : placeholder}
                        />
                      )}
                    </div>
                  </div>
                  <SelectPopover
                    items={listItems}
                    maxHeight={maxHeight}
                    isSearchable={isSearchable}
                    searchPlaceholder={searchPlaceholder ?? t('Select:search.placeholder')}
                    searchAriaLabel={t('Select:search.ariaLabel')}
                    clearAriaLabel={t('searchInput:buttons.ariaLabels.reset')}
                    noResultsLabel={t('Select:noResults')}
                    headerMessage={headerMessage}
                  />
                  <FieldError>
                    <ValidationMessage id={`${inputId}-error`} status="error">
                      {fieldState.error?.message ?? ''}
                    </ValidationMessage>
                  </FieldError>
                </>
              )}
            </AriaSelect>
          </div>
        )
      }}
    />
  )
}

Select.Skeleton = SelectSkeleton
